import { FC, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isNotEmpty } from '../../../../../_metronic/helpers'
import { useListView } from '../core/ListViewProvider'
import { updateShifaImplementation } from '../core/_requests'
import clsx from 'clsx'
import { useQueryResponse } from "../core/QueryResponseProvider";
import axios from "axios";
import { useIntl } from 'react-intl'

type Props = {
  shifaImplementation: any
}

const editSchema = Yup.object().shape({
  phase: Yup.string(),
  siteNumberIdentification: Yup.string(),
  earthquakeAffectedArea: Yup.boolean(),
  criticalPath: Yup.boolean(),
  contingencyInTimeOrTimeExtension: Yup.number(),
  provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications: Yup.date().nullable(),
  defectLiabilityPeriod: Yup.date().nullable(),
  finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications: Yup.date().nullable(),
  exchangeRateAtContractSignatureDate: Yup.number(),
  estimatedProcurementCostTRY: Yup.number(),
  estimatedProcurementCostEUR: Yup.number(),
})

const ShifaImplementationUpdateModalForm: FC<Props> = ({ shifaImplementation }) => {
  const { setItemIdForUpdate, itemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intl = useIntl()

  const formik = useFormik({
    initialValues: {
      siteId: itemIdForUpdate,
      phase: shifaImplementation.phase || '',
      siteNumberIdentification: shifaImplementation.siteNumberIdentification || '',
      earthquakeAffectedArea: shifaImplementation.earthquakeAffectedArea || false,
      criticalPath: shifaImplementation.criticalPath || false,
      contingencyInTimeOrTimeExtension: shifaImplementation.contingencyInTimeOrTimeExtension || 0,
      provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications: shifaImplementation.provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications ? shifaImplementation.provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications.split('T')[0] : '',
      defectLiabilityPeriod: shifaImplementation.defectLiabilityPeriod ? shifaImplementation.defectLiabilityPeriod.split('T')[0] : '',
      finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications: shifaImplementation.finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications ? shifaImplementation.finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications.split('T')[0] : '',
      exchangeRateAtContractSignatureDate: shifaImplementation.exchangeRateAtContractSignatureDate || 0,
      estimatedProcurementCostTRY: shifaImplementation.estimatedProcurementCostTRY || 0,
      estimatedProcurementCostEUR: shifaImplementation.estimatedProcurementCostEUR || 0,
    },
    validationSchema: editSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true)
      setError(null)
      try {
        await updateShifaImplementation(values)
        await refetch()
        setItemIdForUpdate(undefined)
      } catch (ex) {
        console.error(ex)
        setError(intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.UNEXPECTED_ERROR' }))
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  return (
    <>
      <form id='kt_modal_add_user_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        {error && (
          <div className='alert alert-danger d-flex align-items-center mb-7'>
            <i className='ki-duotone ki-shield-cross fs-2hx text-danger me-4'>
              <span className='path1'></span>
              <span className='path2'></span>
              <span className='path3'></span>
            </i>
            <div className='d-flex flex-column'>
              <h4 className='mb-1 text-danger'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.ERROR_TITLE' })}</h4>
              <span>{error}</span>
            </div>
          </div>
        )}
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_user_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_user_header'
          data-kt-scroll-wrappers='#kt_modal_add_user_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* Phase */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.PHASE' })}</label>
            <input
              placeholder={intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.PHASE' })}
              {...formik.getFieldProps('phase')}
              type='text'
              name='phase'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.phase && formik.errors.phase },
                {
                  'is-valid': formik.touched.phase && !formik.errors.phase,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || loading}
            />
            {formik.touched.phase && formik.errors.phase && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.phase as string}</span>
                </div>
              </div>
            )}
          </div>

          {/* Site Number Identification */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.SITE_NUMBER_IDENTIFICATION' })}</label>
            <input
              placeholder={intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.SITE_NUMBER_IDENTIFICATION' })}
              {...formik.getFieldProps('siteNumberIdentification')}
              type='text'
              name='siteNumberIdentification'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.siteNumberIdentification && formik.errors.siteNumberIdentification },
                {
                  'is-valid': formik.touched.siteNumberIdentification && !formik.errors.siteNumberIdentification,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Earthquake Affected Area */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.EARTHQUAKE_AFFECTED_AREA' })}</label>
            <div className='form-check form-check-solid form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                {...formik.getFieldProps('earthquakeAffectedArea')}
                checked={formik.values.earthquakeAffectedArea}
                disabled={formik.isSubmitting || loading}
              />
            </div>
          </div>

          {/* Critical Path */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.CRITICAL_PATH' })}</label>
            <div className='form-check form-check-solid form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                {...formik.getFieldProps('criticalPath')}
                checked={formik.values.criticalPath}
                disabled={formik.isSubmitting || loading}
              />
            </div>
          </div>

          {/* Contingency In Time Or Time Extension */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.CONTINGENCY_IN_TIME' })}</label>
            <input
              placeholder={intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.CONTINGENCY_IN_TIME' })}
              {...formik.getFieldProps('contingencyInTimeOrTimeExtension')}
              type='number'
              name='contingencyInTimeOrTimeExtension'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.contingencyInTimeOrTimeExtension && formik.errors.contingencyInTimeOrTimeExtension },
                {
                  'is-valid': formik.touched.contingencyInTimeOrTimeExtension && !formik.errors.contingencyInTimeOrTimeExtension,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Provisional Acceptance Date */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.PROVISIONAL_ACCEPTANCE_DATE' })}</label>
            <input
              {...formik.getFieldProps('provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications')}
              type='date'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications && formik.errors.provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications },
                {
                  'is-valid': formik.touched.provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications && !formik.errors.provisionalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications,
                }
              )}
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Defect Liability Period */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.DEFECT_LIABILITY_PERIOD' })}</label>
            <input
              {...formik.getFieldProps('defectLiabilityPeriod')}
              type='date'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.defectLiabilityPeriod && formik.errors.defectLiabilityPeriod },
                {
                  'is-valid': formik.touched.defectLiabilityPeriod && !formik.errors.defectLiabilityPeriod,
                }
              )}
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Final Acceptance Date */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.FINAL_ACCEPTANCE_DATE' })}</label>
            <input
              {...formik.getFieldProps('finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications')}
              type='date'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications && formik.errors.finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications },
                {
                  'is-valid': formik.touched.finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications && !formik.errors.finalAcceptanceTechnicalInspectionRemedyDefectsAndCertifications,
                }
              )}
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Exchange Rate */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.EXCHANGE_RATE' })}</label>
            <input
              placeholder={intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.EXCHANGE_RATE' })}
              {...formik.getFieldProps('exchangeRateAtContractSignatureDate')}
              type='number'
              step='0.0001'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.exchangeRateAtContractSignatureDate && formik.errors.exchangeRateAtContractSignatureDate },
                {
                  'is-valid': formik.touched.exchangeRateAtContractSignatureDate && !formik.errors.exchangeRateAtContractSignatureDate,
                }
              )}
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Estimated Procurement Cost TRY */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.ESTIMATED_COST_TRY' })}</label>
            <input
              placeholder={intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.ESTIMATED_COST_TRY' })}
              {...formik.getFieldProps('estimatedProcurementCostTRY')}
              type='number'
              step='0.01'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.estimatedProcurementCostTRY && formik.errors.estimatedProcurementCostTRY },
                {
                  'is-valid': formik.touched.estimatedProcurementCostTRY && !formik.errors.estimatedProcurementCostTRY,
                }
              )}
              disabled={formik.isSubmitting || loading}
            />
          </div>

          {/* Estimated Procurement Cost EUR */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>{intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.ESTIMATED_COST_EUR' })}</label>
            <input
              placeholder={intl.formatMessage({ id: 'SHIFA.UPDATE.MODAL.ESTIMATED_COST_EUR' })}
              {...formik.getFieldProps('estimatedProcurementCostEUR')}
              type='number'
              step='0.01'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.estimatedProcurementCostEUR && formik.errors.estimatedProcurementCostEUR },
                {
                  'is-valid': formik.touched.estimatedProcurementCostEUR && !formik.errors.estimatedProcurementCostEUR,
                }
              )}
              disabled={formik.isSubmitting || loading}
            />
          </div>

        </div>
        {/* Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => setItemIdForUpdate(undefined)}
            className='btn btn-light me-3'
            data-kt-users-modal-action='cancel'
            disabled={formik.isSubmitting || loading}
          >
            {intl.formatMessage({ id: 'DISCARD' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-users-modal-action='submit'
          // disabled={loading || formik.isSubmitting || !formik.isValid || !formik.dirty}
          >
            <span className='indicator-label'>{intl.formatMessage({ id: 'SUBMIT' })}</span>
            {(formik.isSubmitting || loading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'PLEASE.WAIT' })}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </>
  )
}

export { ShifaImplementationUpdateModalForm }
